import { Link } from "react-router-dom";
import type { AnimePost } from "@/mocks/anime";

const catColor: Record<string, string> = {
  recommend: "bg-primary-100 text-primary-800",
  avoid: "bg-secondary-100 text-secondary-800",
  order: "bg-accent-100 text-accent-800",
  review: "bg-primary-100 text-primary-800",
};

const catLabel: Record<string, string> = {
  recommend: "求番推荐",
  avoid: "避雷指南",
  order: "补番顺序",
  review: "漫评讨论",
};

interface PostCardProps {
  post: AnimePost;
  horizontal?: boolean;
}

export default function PostCard({ post, horizontal = false }: PostCardProps) {
  if (horizontal) {
    return (
      <article className="card group flex overflow-hidden transition-colors duration-200 hover:border-primary-300">
        <Link to={`/${post.category}`} className="relative block h-auto w-40 shrink-0 overflow-hidden sm:w-52" aria-label={post.title}>
          <img
            src={post.image}
            alt={post.title}
            title={post.title}
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
          />
          {post.badge && (
            <span className="absolute left-2 top-2 rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-bold text-background-50">
              {post.badge}
            </span>
          )}
        </Link>
        <div className="flex flex-col justify-center gap-2 p-4 md:p-5">
          <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-bold ${catColor[post.category]}`}>
            {catLabel[post.category]}
          </span>
          <h3 className="text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-800 md:text-base">
            <Link to={`/${post.category}`}>{post.title}</Link>
          </h3>
          <p className="line-clamp-2 text-xs leading-5 text-foreground-600 md:text-sm">{post.excerpt}</p>
          <div className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {post.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {post.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-3-line text-primary-500"></i>
              {post.likes}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="card group flex h-full flex-col overflow-hidden transition-colors duration-200 hover:border-primary-300">
      <Link to={`/${post.category}`} className="relative block aspect-[16/10] overflow-hidden" aria-label={post.title}>
        <img
          src={post.image}
          alt={post.title}
          title={post.title}
          className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />
        {post.badge && (
          <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-bold text-background-50">
            {post.badge}
          </span>
        )}
        {post.rating && (
          <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-foreground-950/70 px-2.5 py-0.5 text-xs font-bold text-accent-300 backdrop-blur">
            <i className="ri-star-fill text-accent-400"></i>
            {post.rating.toFixed(1)}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-bold ${catColor[post.category]}`}>
          {catLabel[post.category]}
        </span>
        <h3 className="mt-2.5 text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-800 md:text-base">
          <Link to={`/${post.category}`}>{post.title}</Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-xs leading-5 text-foreground-600 md:text-sm">{post.excerpt}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {post.author}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {post.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-3-line text-primary-500"></i>
              {post.likes}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}